import { AlertTriangle, X } from 'lucide-react';
import { useEffect } from 'react';

interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

interface ErrorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  error: ApiError | null;
  title?: string;
}

export function ErrorDialog({ 
  isOpen, 
  onClose, 
  error, 
  title = "Error Saving Workflow" 
}: ErrorDialogProps) {
  // Close dialog on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !error) return null;

  // Handle both single message and array of messages
  const messages = Array.isArray(error.message) ? error.message : [error.message];

  // Get appropriate error context based on status code
  const getErrorContext = (statusCode?: number) => {
    switch (statusCode) {
      case 400:
        return {
          type: 'Validation Error',
          description: 'Please check the following issues and try again:'
        };
      case 401:
        return {
          type: 'Authentication Error',
          description: 'You are not authorized to perform this action:'
        };
      case 403:
        return {
          type: 'Permission Error',
          description: 'You do not have permission to save workflows:'
        };
      case 404:
        return {
          type: 'Not Found',
          description: 'The requested resource was not found:'
        };
      case 409:
        return {
          type: 'Conflict Error',
          description: 'There was a conflict with your request:'
        };
      case 429:
        return {
          type: 'Rate Limit Error',
          description: 'Too many requests. Please wait and try again:'
        };
      case 500:
      case 502:
      case 503:
        return {
          type: 'Server Error',
          description: 'A server error occurred. Please try again later:'
        };
      default:
        return {
          type: 'Error',
          description: 'An error occurred while saving your workflow:'
        };
    }
  };

  const errorContext = getErrorContext(error.statusCode);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70">
      <div className="mx-4 w-full max-w-md bg-white rounded-lg border shadow-xl dark:bg-gray-800 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Type and Status */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                {errorContext.type}
              </span>
              {error.statusCode && (
                <span className="px-2 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                  {error.statusCode}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {errorContext.description}
            </p>
          </div>

          {/* Error Messages */}
          <div className="space-y-2">
            {messages.map((message, index) => (
              <div 
                key={index}
                className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
              >
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-red-500 rounded-full mt-2"></div>
                <p className="text-sm text-red-800 dark:text-red-200 leading-relaxed">
                  {message}
                </p>
              </div>
            ))}
          </div>

          {/* Additional Help */}
          {error.statusCode === 400 && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                💡 Common Solutions
              </h4>
              <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                <li>• Ensure your workflow has at least one processing node</li>
                <li>• Check that all required fields are filled out</li>
                <li>• Verify node connections are properly configured</li>
                <li>• Remove any invalid or deprecated node types</li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
          >
            Close
          </button>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="px-6 pb-4 text-xs text-gray-500 dark:text-gray-400">
          Press <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">Esc</kbd> to close
        </div>
      </div>
    </div>
  );
} 